/**
 * Session Timeout — banking-style idle + absolute limits on client.
 *
 * Comportamento:
 *  - Login sem "lembrar":  30 min idle / 8 h absoluto
 *  - Login com "lembrar":  2 h idle / 7 dias absoluto
 *  - Ao expirar: signOut({ scope: 'local' }) — tokens do browser somem mas
 *    refresh token continua válido no servidor, preservando a subscription
 *    de push (SW ainda recebe notificações até a próxima sessão).
 *  - Aviso toast 2 min antes do idle timeout.
 *
 * Storage: localStorage em todos os casos. Required para Web Push persistir
 * entre fechamentos do app. O modo "ephemeral" não é feito por storage e sim
 * por timeout curto.
 */

import { supabase } from '@/integrations/supabase/client';

// ---------------------------------------------------------------
// Constants (em ms)
// ---------------------------------------------------------------

const MIN = 60 * 1000;
const HOUR = 60 * MIN;
const DAY = 24 * HOUR;

export const SESSION_LIMITS = {
  ephemeral:  { idle: 30 * MIN, absolute: 8 * HOUR },
  remembered: { idle: 2 * HOUR,  absolute: 7 * DAY },
} as const;

export const WARN_BEFORE_EXPIRE = 2 * MIN;
const CHECK_INTERVAL = 30 * 1000;
const ACTIVITY_DEBOUNCE = 30 * 1000;
const WARN_COOLDOWN = 60 * 1000;

const KEYS = {
  remember: 'bf.session.remember',
  sessionStart: 'bf.session.start',
  lastActivity: 'bf.session.last_activity',
} as const;

// ---------------------------------------------------------------
// Session policy
// ---------------------------------------------------------------

/**
 * Grava a política de sessão no login. Reinicia session_start + last_activity.
 */
export function setSessionPolicy(rememberMe: boolean): void {
  const now = Date.now().toString();
  localStorage.setItem(KEYS.remember, rememberMe ? 'true' : 'false');
  localStorage.setItem(KEYS.sessionStart, now);
  localStorage.setItem(KEYS.lastActivity, now);
}

/**
 * Limpa tudo (chamado no signOut).
 */
export function clearSessionPolicy(): void {
  localStorage.removeItem(KEYS.remember);
  localStorage.removeItem(KEYS.sessionStart);
  localStorage.removeItem(KEYS.lastActivity);
}

export function isRemembered(): boolean {
  return localStorage.getItem(KEYS.remember) === 'true';
}

function getLimits() {
  return isRemembered() ? SESSION_LIMITS.remembered : SESSION_LIMITS.ephemeral;
}

// ---------------------------------------------------------------
// Timeout check
// ---------------------------------------------------------------

export type ExpireReason = 'idle' | 'absolute';

export interface TimeoutStatus {
  expired: boolean;
  reason?: ExpireReason;
  warnSoon: boolean;
  msUntilExpire: number;
}

export function checkTimeout(): TimeoutStatus {
  const start = Number(localStorage.getItem(KEYS.sessionStart) || 0);
  const last  = Number(localStorage.getItem(KEYS.lastActivity) || 0);

  if (!start || !last) {
    // Política ausente → assume sessão válida. Pode acontecer em sessões criadas
    // antes deste código ser deployado; trataremos como "ephemeral" ao próximo
    // login.
    return { expired: false, warnSoon: false, msUntilExpire: Infinity };
  }

  const { idle, absolute } = getLimits();
  const now = Date.now();

  const absElapsed  = now - start;
  const idleElapsed = now - last;

  if (absElapsed >= absolute) {
    return { expired: true, reason: 'absolute', warnSoon: false, msUntilExpire: 0 };
  }
  if (idleElapsed >= idle) {
    return { expired: true, reason: 'idle', warnSoon: false, msUntilExpire: 0 };
  }

  const msIdleRemain = idle - idleElapsed;
  const msAbsRemain  = absolute - absElapsed;
  const msUntilExpire = Math.min(msIdleRemain, msAbsRemain);

  return {
    expired: false,
    warnSoon: msUntilExpire <= WARN_BEFORE_EXPIRE,
    msUntilExpire,
  };
}

// ---------------------------------------------------------------
// Activity tracking (debounced)
// ---------------------------------------------------------------

let activityTimer: number | null = null;

export function touchActivity(): void {
  if (activityTimer != null) return;
  activityTimer = window.setTimeout(() => {
    localStorage.setItem(KEYS.lastActivity, Date.now().toString());
    activityTimer = null;
  }, ACTIVITY_DEBOUNCE);
}

/**
 * Força atualização de last_activity imediatamente (ex: user clicou em
 * "continuar" no aviso de expiração).
 */
export function renewActivityNow(): void {
  if (activityTimer != null) {
    clearTimeout(activityTimer);
    activityTimer = null;
  }
  localStorage.setItem(KEYS.lastActivity, Date.now().toString());
}

// ---------------------------------------------------------------
// Monitor
// ---------------------------------------------------------------

export interface MonitorHandlers {
  onExpire: (reason: ExpireReason) => void;
  onWarn:   (msRemaining: number) => void;
}

let monitorInterval: number | null = null;
const activityCleanups: Array<() => void> = [];
let lastWarnAt = 0;

export function startSessionMonitor(handlers: MonitorHandlers): void {
  stopSessionMonitor();

  // Ativa listeners de atividade
  const events: Array<keyof DocumentEventMap> = ['click', 'keydown', 'mousemove', 'touchstart'];
  const handler = () => touchActivity();
  for (const ev of events) {
    document.addEventListener(ev, handler, { passive: true });
    activityCleanups.push(() => document.removeEventListener(ev, handler));
  }

  // Loop de verificação
  monitorInterval = window.setInterval(() => {
    const s = checkTimeout();
    if (s.expired) {
      handlers.onExpire(s.reason!);
      stopSessionMonitor();
      return;
    }
    if (s.warnSoon && Date.now() - lastWarnAt > WARN_COOLDOWN) {
      handlers.onWarn(s.msUntilExpire);
      lastWarnAt = Date.now();
    }
  }, CHECK_INTERVAL);
}

export function stopSessionMonitor(): void {
  if (monitorInterval != null) {
    clearInterval(monitorInterval);
    monitorInterval = null;
  }
  for (const cleanup of activityCleanups) cleanup();
  activityCleanups.length = 0;
  lastWarnAt = 0;
  if (activityTimer != null) {
    clearTimeout(activityTimer);
    activityTimer = null;
  }
}

// ---------------------------------------------------------------
// Expire action
// ---------------------------------------------------------------

/**
 * Desloga apenas localmente (scope: 'local') — refresh token no servidor
 * continua válido para que a subscription de push permaneça operante. Push
 * notifications continuam chegando até re-login.
 */
export async function signOutDueToTimeout(): Promise<void> {
  clearSessionPolicy();
  try {
    await supabase.auth.signOut({ scope: 'local' });
  } catch (err) {
    console.warn('signOutDueToTimeout: signOut falhou:', err);
  }
}
